import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import {
  Users, Loader2, CalendarDays, ChevronLeft, ChevronRight,
  Bed, ShieldCheck, ShieldX, Ticket, Download, TrendingUp,
} from 'lucide-react';
import { getStaffActivity, getStaff } from '../services/adminService';

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt   = (n) => Number(n || 0).toLocaleString('en-US');
const money = (n) => `৳${fmt(n)}`;
const fmtDT = (d) => new Date(d).toLocaleString('en-BD', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
});

const todayISO        = () => new Date().toISOString().slice(0, 10);
const mondayISO       = () => { const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.toISOString().slice(0, 10); };
const firstOfMonthISO = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

const ACTION_META = {
  Booking:        { label: 'Walk-in Booking',   color: 'bg-blue-600/80 text-blue-100',   icon: <Bed         className="h-3 w-3" /> },
  ConfirmPayment: { label: 'Payment Confirmed', color: 'bg-green-600/80 text-green-100', icon: <ShieldCheck className="h-3 w-3" /> },
  DeclinePayment: { label: 'Payment Declined',  color: 'bg-red-600/80 text-red-100',     icon: <ShieldX     className="h-3 w-3" /> },
  TicketSale:     { label: 'Ticket Sale',       color: 'bg-amber-600/80 text-amber-100', icon: <Ticket      className="h-3 w-3" /> },
};

const ActionBadge = ({ type }) => {
  const m = ACTION_META[type] || { label: type, color: 'bg-slate-600/80 text-slate-100', icon: null };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${m.color}`}>
      {m.icon}{m.label}
    </span>
  );
};

const Th = ({ children, right }) => (
  <th className={`px-3 py-2.5 text-xs font-semibold text-slate-300 bg-slate-700/80 border-b border-slate-600 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);
const Td = ({ children, right, mono, accent, dim }) => (
  <td className={`px-3 py-2 text-xs border-b border-slate-700/40 ${right ? 'text-right' : ''} ${mono ? 'font-mono' : ''} ${accent || (dim ? 'text-slate-500' : 'text-slate-300')}`}>
    {children}
  </td>
);

const exportCsv = (items, staffName) => {
  const rows = [
    ['Action', 'Staff', 'Reference', 'Guest', 'Amount (৳)', 'Rooms', 'Reason', 'Date/Time'],
    ...items.map(r => [
      ACTION_META[r.actionType]?.label || r.actionType,
      r.staffName, r.reference || '', r.guestName || '',
      r.amount != null ? r.amount : '', r.rooms != null ? r.rooms : '',
      r.reason || '', fmtDT(r.at),
    ]),
  ];
  const csv  = rows.map(row => row.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `Staff_Activity${staffName ? `_${staffName}` : ''}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ── Main ──────────────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;
const PRESETS   = [['today','Today'],['week','This Week'],['month','This Month'],['custom','Custom']];

const StaffActivity = () => {
  const [staffFilter, setStaffFilter] = useState('__all__');
  const [preset,      setPreset]      = useState('today');
  const [dateFrom,    setDateFrom]    = useState(todayISO());
  const [dateTo,      setDateTo]      = useState(todayISO());
  const [page,        setPage]        = useState(1);

  const [activityTab, setActivityTab] = useState('reservation'); // 'reservation' | 'ticket'

  const [staffList, setStaffList] = useState([]);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    getStaff().then(list => setStaffList(list || [])).catch(() => {});
  }, []);

  const applyPreset = (p) => {
    setPreset(p); setPage(1);
    if      (p === 'today') { setDateFrom(todayISO());        setDateTo(todayISO()); }
    else if (p === 'week')  { setDateFrom(mondayISO());       setDateTo(todayISO()); }
    else if (p === 'month') { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }
  };

  const isSpecific = staffFilter !== '__all__';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getStaffActivity({
        actionType: 'All',
        staffName:  isSpecific ? staffFilter : undefined,
        dateFrom,
        dateTo,
        page:     isSpecific ? 1 : page,
        pageSize: isSpecific ? 500 : PAGE_SIZE,
      });
      setData(result);
    } catch (e) {
      toast({ title: 'Failed to load staff activity', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [isSpecific, staffFilter, dateFrom, dateTo, page, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const summary     = data?.summary     || [];
  const activityLog = data?.activityLog || [];
  const totalCount  = data?.totalCount  || 0;
  const totalPages  = Math.ceil(totalCount / PAGE_SIZE);

  const selected     = isSpecific && summary.length > 0 ? summary[0] : null;
  const hasReservation = ((selected?.totalPaymentsConfirmed || 0) + (selected?.totalBookings || 0)) > 0;
  const hasTickets     = (selected?.totalTicketsSold || 0) > 0;

  // Auto-default tab when staff changes
  useEffect(() => {
    if (!hasTickets && hasReservation) setActivityTab('reservation');
    else if (hasTickets && !hasReservation) setActivityTab('ticket');
    else setActivityTab('reservation');
  }, [staffFilter]); // eslint-disable-line

  // Client-side split of the activity log
  const reservationLog = activityLog.filter(r => ['Booking', 'ConfirmPayment', 'DeclinePayment'].includes(r.actionType));
  const ticketLog      = activityLog.filter(r => r.actionType === 'TicketSale');
  const displayedLog   = isSpecific ? (activityTab === 'ticket' ? ticketLog : reservationLog) : activityLog;


  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="h-6 w-6 text-blue-400" /> Staff Activity
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">Track what each staff member did and how much they collected</p>
        </div>
        {isSpecific && activityLog.length > 0 && (
          <Button variant="outline" size="sm"
            onClick={() => exportCsv(activityLog, staffFilter)}
            className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        )}
      </div>

      {/* ── Controls ── */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">

            {/* Staff picker */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-400 font-medium">Staff Member</label>
              <Select value={staffFilter} onValueChange={v => { setStaffFilter(v); setPage(1); }}>
                <SelectTrigger className="w-56 bg-slate-700 border-slate-600 text-white h-9">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="__all__">All Staff</SelectItem>
                  {staffList.map(s => (
                    <SelectItem key={s.id} value={s.fullName || s.email}>
                      {s.fullName || s.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period */}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-400 font-medium">Period</label>
              <div className="flex items-center gap-1 bg-slate-700 border border-slate-600 rounded-lg p-1">
                {PRESETS.map(([p, label]) => (
                  <button key={p} onClick={() => applyPreset(p)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      preset === p ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom date inputs */}
            {preset === 'custom' && (
              <>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-400 font-medium">From</label>
                  <input type="date" value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    className="h-9 px-3 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-slate-400 font-medium">To</label>
                  <input type="date" value={dateTo}
                    onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    className="h-9 px-3 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                </div>
              </>
            )}

          </div>
        </CardContent>
      </Card>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        </div>
      )}

      {!loading && (
        <>
          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* SPECIFIC STAFF VIEW                                               */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          {isSpecific && (
            <>
              {selected ? (
                <>
                  {/* Revenue hero */}
                  <Card className="bg-gradient-to-r from-blue-950/60 to-slate-800/60 border-blue-500/40">
                    <CardContent className="p-5 flex flex-wrap items-center gap-6">
                      <div className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                        {selected.staffName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Total Revenue</p>
                        <p className="text-white font-bold text-3xl">{money(selected.totalRevenue)}</p>
                        <p className="text-blue-300 text-sm mt-0.5">{selected.staffName} · confirmed money only</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 4 stat cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <Bed className="h-5 w-5 text-blue-400 mx-auto mb-1.5" />
                        <p className="text-2xl font-bold text-white">{selected.totalBookings}</p>
                        <p className="text-slate-400 text-xs mt-0.5">Walk-in Bookings</p>
                        <p className="text-slate-500 text-xs mt-0.5">revenue via confirmation</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <ShieldCheck className="h-5 w-5 text-green-400 mx-auto mb-1.5" />
                        <p className="text-2xl font-bold text-white">{selected.totalPaymentsConfirmed}</p>
                        <p className="text-slate-400 text-xs mt-0.5">Payments Confirmed</p>
                        <p className="text-green-300 text-xs font-semibold mt-0.5">{money(selected.totalAmountConfirmed)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <Ticket className="h-5 w-5 text-amber-400 mx-auto mb-1.5" />
                        <p className="text-2xl font-bold text-white">{selected.totalTicketsSold}</p>
                        <p className="text-slate-400 text-xs mt-0.5">Tickets Sold</p>
                        <p className="text-amber-300 text-xs font-semibold mt-0.5">{money(selected.totalTicketValue)}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/50 border-slate-700">
                      <CardContent className="p-4 text-center">
                        <ShieldX className="h-5 w-5 text-red-400 mx-auto mb-1.5" />
                        <p className="text-2xl font-bold text-white">{selected.totalPaymentsDeclined}</p>
                        <p className="text-slate-400 text-xs mt-0.5">Payments Declined</p>
                        <p className="text-slate-500 text-xs mt-0.5">not counted in revenue</p>
                      </CardContent>
                    </Card>
                  </div>
                </>
              ) : (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="py-14 text-center text-slate-500">
                    <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>No activity found for <span className="text-slate-300">{staffFilter}</span> in this period.</p>
                  </CardContent>
                </Card>
              )}

              {/* Activity log with optional toggle */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-slate-400" />
                      Activity Log
                      <span className="text-slate-500 font-normal text-xs ml-1">({displayedLog.length} records)</span>
                    </CardTitle>
                    {/* Toggle only when staff has both ticket + reservation activity */}
                    {hasTickets && hasReservation && (
                      <div className="flex items-center gap-1 bg-slate-700 border border-slate-600 rounded-lg p-1">
                        {[['reservation','Reservation', <Bed className="h-3.5 w-3.5" />], ['ticket','Ticket', <Ticket className="h-3.5 w-3.5" />]].map(([val, label, icon]) => (
                          <button key={val} onClick={() => setActivityTab(val)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                              activityTab === val ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
                            }`}>
                            {icon}{label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </CardHeader>
              {displayedLog.length === 0 ? (
                <CardContent className="py-10 text-center text-slate-500">
                  <p>No activity in this period.</p>
                </CardContent>
              ) : (
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr>
                            <Th>#</Th>
                            <Th>Action</Th>
                            <Th>Done By</Th>
                            <Th>Reference</Th>
                            <Th>Guest</Th>
                            <Th right>Amount</Th>
                            <Th right>Rooms</Th>
                            <Th>Reason</Th>
                            <Th>Date / Time</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedLog.map((row, i) => (
                            <tr key={i} className={i % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-800/60'}>
                              <Td dim>{i + 1}</Td>
                              <Td><ActionBadge type={row.actionType} /></Td>
                              <Td accent="text-white font-medium">{row.staffName || '—'}</Td>
                              <Td mono accent="text-blue-400">{row.reference || '—'}</Td>
                              <Td>{row.guestName || '—'}</Td>
                              <Td right accent={row.amount != null && row.amount > 0 ? 'text-green-400 font-semibold' : 'text-slate-500'}>
                                {row.amount != null && row.amount > 0 ? money(row.amount) : '—'}
                              </Td>
                              <Td right accent={row.rooms != null ? 'text-purple-400' : 'text-slate-500'}>
                                {row.rooms != null ? row.rooms : '—'}
                              </Td>
                              <Td accent={row.reason ? 'text-red-400' : 'text-slate-500'}>{row.reason || '—'}</Td>
                              <Td dim>{fmtDT(row.at)}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
                        <p className="text-xs text-slate-500">Page {page} of {totalPages} · {totalCount} records</p>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8 w-8 p-0">
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-slate-300 text-sm font-medium px-2">{page}</span>
                          <Button variant="outline" size="sm" disabled={page >= totalPages}
                            onClick={() => setPage(p => p + 1)}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8 w-8 p-0">
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                </CardContent>
              )}
              </Card>
            </>
          )}

          {/* ══════════════════════════════════════════════════════════════════ */}
          {/* ALL STAFF VIEW                                                    */}
          {/* ══════════════════════════════════════════════════════════════════ */}
          {!isSpecific && (
            <>
              {summary.length === 0 ? (
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="py-14 text-center text-slate-500">
                    <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>No staff activity found for this period.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {/* Total revenue across all staff */}
                  <Card className="bg-gradient-to-r from-green-950/60 to-slate-800/60 border-green-500/40">
                    <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-6 w-6 text-green-400 flex-shrink-0" />
                        <div>
                          <p className="text-slate-400 text-xs font-medium uppercase tracking-wide">Total Staff Revenue</p>
                          <p className="text-white font-bold text-2xl">
                            {money(summary.reduce((sum, s) => sum + (s.totalRevenue || 0), 0))}
                          </p>
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm">
                        {summary.length} staff member{summary.length !== 1 ? 's' : ''} active in this period
                      </p>
                    </CardContent>
                  </Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {summary.map(s => (
                      <Card key={s.staffName}
                        className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-blue-500/60 transition-all"
                        onClick={() => { setStaffFilter(s.staffName); setPage(1); }}>
                        <CardContent className="p-4">
                          {/* Name header */}
                          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-700">
                            <div className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                              {s.staffName?.charAt(0)?.toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-white text-base leading-tight truncate">{s.staffName || '—'}</p>
                              <div className="flex items-center gap-1 mt-1">
                                <TrendingUp className="h-3.5 w-3.5 text-green-400" />
                                <p className="text-green-400 text-sm font-bold">{money(s.totalRevenue)}</p>
                                <span className="text-slate-500 text-xs">revenue</span>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5 text-xs">
                            <div className="bg-slate-700/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                              <Bed className="h-3 w-3 text-blue-400 flex-shrink-0" />
                              <span className="text-slate-400">Walk-ins</span>
                              <span className="text-white font-semibold ml-auto">{s.totalBookings}</span>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                              <ShieldCheck className="h-3 w-3 text-green-400 flex-shrink-0" />
                              <span className="text-slate-400">Confirmed</span>
                              <span className="text-white font-semibold ml-auto">{s.totalPaymentsConfirmed}</span>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                              <Ticket className="h-3 w-3 text-amber-400 flex-shrink-0" />
                              <span className="text-slate-400">Tickets</span>
                              <span className="text-white font-semibold ml-auto">{s.totalTicketsSold}</span>
                            </div>
                            <div className="bg-slate-700/50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
                              <ShieldX className="h-3 w-3 text-red-400 flex-shrink-0" />
                              <span className="text-slate-400">Declined</span>
                              <span className="text-white font-semibold ml-auto">{s.totalPaymentsDeclined}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default StaffActivity;
